import {CompletionContext} from 'contexts/CompletionContext';
import {ProfileContext} from 'contexts/ProfileContext';
import {getOwnSectionId} from 'contexts/profileUtil';
import {getSectionDebriefItemTarget} from 'course/section/postSectionSurveys/sectionDebriefRoadmapItem';
import {useSectionDebriefProgress} from 'course/section/postSectionSurveys/useSectionDebriefProgress';
import {getSectionFeedbackCompletionKey} from 'course/section/slSectionFeedback/sectionFeedbackRoadmapItem';
import {ROADMAP} from 'course/editors/roadmapEditor/roadmapSchema';
import {
  getOneOfChoices,
  isOneOfComplete,
} from 'course/editors/roadmapEditor/roadmapOneOf';
import {useSectionWeekCredit} from 'course/section/useSectionWeekCredit';
import {useCourseId} from 'hooks/router/useUrlParams';
import {useAuthUid} from 'hooks/user/useUserId';
import {useSectionShadowRoadmapProgress} from 'course/shadowing/hooks/useSectionShadowRoadmapProgress';
import {isSectionShadowItemComplete} from 'course/shadowing/sectionShadowRoadmapItem';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
import {useState, useEffect, useContext} from 'react';

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
export const useNextIncompleteModuleId = moduleId => {};

export const useCompletionMap = todoItemList => {
  const {
    isAssnCompleted,
    isLessonCompleted,
    isSelfCheckCompleted,
    isSectionCompleted,
  } = useContext(CompletionContext);

  const hasShadowItem = (todoItemList ?? []).some(
    todo => todo.itemType === ROADMAP.SECTION_SHADOW.key,
  );
  const shadowProgress = useSectionShadowRoadmapProgress({
    isEnabled: hasShadowItem,
  });
  const hasSectionItem = (todoItemList ?? []).some(
    todo => todo.itemType === ROADMAP.SECTION.key,
  );
  const authUid = useAuthUid();
  const courseId = useCourseId();
  const sectionCredit = useSectionWeekCredit({
    uid: hasSectionItem ? (authUid ?? undefined) : undefined,
    courseId,
  });
  const {userData} = useContext(ProfileContext);
  const ownSectionId = getOwnSectionId(userData);
  const getDebriefTarget = todo =>
    todo.debriefTarget ??
    getSectionDebriefItemTarget({
      completionId: todo.completionId,
      ownSectionId,
    });
  const debriefItems = (todoItemList ?? []).filter(
    todo => todo.itemType === ROADMAP.SECTION_DEBRIEF.key,
  );
  const debriefProgress = useSectionDebriefProgress({
    isEnabled: debriefItems.length > 0,
    targets: debriefItems
      .filter(todo => !todo.isMissed)
      .map(getDebriefTarget)
      .filter(Boolean),
  });
  const completionMap = {};

  const checkIsComplete = todo => {
    switch (todo.itemType) {
      case ROADMAP.AUTO_CHECK.key:
        return true;
      case ROADMAP.LESSON.key:
        return isLessonCompleted(todo.completionId);
      case ROADMAP.ASSIGNMENT.key:
      case ROADMAP.MATH_ASSIGNMENT.key:
        return isAssnCompleted(todo.completionId);
      case ROADMAP.ONE_OF.key:
        return isOneOfComplete({
          choices: getOneOfChoices(todo),
          isAssnCompleted,
        });
      case ROADMAP.UNTIED_LINK.key:
      case ROADMAP.COMPLETION_LINK.key:
        return isSelfCheckCompleted(todo.completionId);
      case ROADMAP.SECTION.key:
        // A section week is done exactly when the certificate counts it.
        if (sectionCredit.isLoading) {
          return isSectionCompleted(todo.completionId);
        }
        return (
          sectionCredit.credit.creditedAttendance[String(todo.completionId)] ===
          true
        );
      case ROADMAP.SECTION_SHADOW.key:
        return (
          shadowProgress.isLoaded &&
          isSectionShadowItemComplete({
            completionId: todo.completionId,
            assignments: shadowProgress.assignments,
            requiredPerWeek: shadowProgress.requiredPerWeek,
          })
        );
      case ROADMAP.SECTION_FEEDBACK.key: {
        const key = getSectionFeedbackCompletionKey(todo.completionId);
        return Boolean(key) && Boolean(isSelfCheckCompleted(key));
      }
      case ROADMAP.SECTION_DEBRIEF.key: {
        if (todo.isMissed) return false;
        const target = getDebriefTarget(todo);
        return (
          Boolean(target) &&
          debriefProgress.isLoaded &&
          debriefProgress.isSubmitted(target)
        );
      }
    }
  };

  if (todoItemList) {
    for (let todo of todoItemList) {
      const isComplete = checkIsComplete(todo);
      completionMap[todo.id] = isComplete;
    }
  }

  return {completionMap};
};
